import React from 'react';
import type { Editor } from 'ckeditor5';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CustomPageVM } from './custom-page.viewmodel';
import { FormFieldInputText } from '../../../../../../global/form/form-field-input/form-field-input-text';
import { FormField } from '../../../../../../global/form/form-field/form-field';
import { OEToggle } from '../../../../../../global/oe-toggle/oe-toggle';
import { RichTextEditor } from '../../../../../../global/rich-text-editor';
import {
    CUSTOM_PAGE_DESCRIPTION_LABEL_TEXT,
    CUSTOM_PAGE_DISPLAY_NAME_LABEL_TEXT,
    CUSTOM_PAGE_ENABLED_LABEL_TEXT,
    CUSTOM_PAGE_NAME_LABEL_TEXT,
    CUSTOM_PAGE_PATH_LABEL_TEXT,
} from '../../../../../event-hubs.text';
import { DESCRIPTION, DISPLAY_NAME, ENABLED, PAGE_NAME, PAGE_PATH } from './custom-page.config';
import { EnablementStatus } from '../../../../../../../constants/common.constants';
import { ModulesSection } from '../_modules/modules-section';
import './custom-page.scss';

type CustomPagePresentationProps = {
    vm: CustomPageVM;
};

export const CustomPagePresentation: React.FC<CustomPagePresentationProps> = observer(({ vm }) => {
    const { formatMessage } = useIntl();
    const enabledField = vm.getFormVmValuePropsByKey(ENABLED);
    const descriptionField = vm.getFormVmValuePropsByKey(DESCRIPTION);

    const handleDescriptionChange = (_: unknown, editor: Editor) => descriptionField.setValue(editor.getData());
    const handleEnabledToggle = () => enabledField.setValue(!enabledField.value);
    const enabledStatusSuffix = enabledField.value ? EnablementStatus.ENABLED : EnablementStatus.DISABLED;
    const hasDescriptionError = !descriptionField.valid && descriptionField.touched;

    return (
        <div className='custom-page'>
            <FormFieldInputText
                field={vm.getFormVmValuePropsByKey(PAGE_NAME)}
                labelDescriptor={CUSTOM_PAGE_NAME_LABEL_TEXT}
                required
                withError
            />
            <FormFieldInputText
                field={vm.getFormVmValuePropsByKey(DISPLAY_NAME)}
                labelDescriptor={CUSTOM_PAGE_DISPLAY_NAME_LABEL_TEXT}
                withError
            />
            <FormFieldInputText
                field={vm.getFormVmValuePropsByKey(PAGE_PATH)}
                labelDescriptor={CUSTOM_PAGE_PATH_LABEL_TEXT}
                additionalClassName='custom-page__path-field'
                required
                withError
            />
            <FormField
                className='m-bottom-20px'
                label={<FormattedMessage {...CUSTOM_PAGE_DESCRIPTION_LABEL_TEXT} />}
            >
                <RichTextEditor
                    value={descriptionField.value as string || ''}
                    onBlur={descriptionField.setTouched}
                    onChange={handleDescriptionChange}
                    validationError={
                        hasDescriptionError &&
                        <FormattedMessage
                            values={{ fieldName: formatMessage(CUSTOM_PAGE_DESCRIPTION_LABEL_TEXT) }}
                            {...descriptionField.validationMessage}
                        />
                    }
                />
            </FormField>
            <div className='m-bottom-20px flex'>
                <OEToggle
                    isSelected={enabledField.value as boolean}
                    onClick={handleEnabledToggle}
                />
                <FormattedMessage
                    values={{ suffix: enabledStatusSuffix }}
                    {...CUSTOM_PAGE_ENABLED_LABEL_TEXT}
                />
            </div>

            <ModulesSection vm={vm.modulesVM} />
        </div>
    );
});
